import { supabase } from '@/lib/supabase'

interface LocationStats {
  location_name: string
  total_reviews: number
  average_rating: number
  five_star: number
  four_star: number
  three_star: number
  two_star: number
  one_star: number
  response_rate: number
  latest_review_date: string
  oldest_review_date: string
}

interface TimeSeriesData {
  period: string
  review_count: number
  average_rating: number
  response_rate: number
}

interface ReviewTrends {
  daily: TimeSeriesData[]
  weekly: TimeSeriesData[]
  monthly: TimeSeriesData[]
  yearly: TimeSeriesData[]
}

interface EnhancedReviewStats {
  overall_performance: {
    total_reviews: number
    average_rating: number
    response_rate: number
    review_velocity_30d: number
    review_velocity_change: number
  }
  location_breakdown: LocationStats[]
  rating_distribution: {
    five_star: { count: number; percentage: number }
    four_star: { count: number; percentage: number }
    three_star: { count: number; percentage: number }
    two_star: { count: number; percentage: number }
    one_star: { count: number; percentage: number }
  }
  time_analysis: {
    reviews_by_month: TimeSeriesData[]
    reviews_by_quarter: TimeSeriesData[]
    reviews_by_year: TimeSeriesData[]
    peak_review_days: string[]
    response_time_analysis: {
      average_response_time_hours: number
      median_response_time_hours: number
      overdue_responses: number
    }
  }
  performance_insights: {
    top_performing_locations: string[]
    locations_needing_attention: string[]
    recent_rating_trends: 'improving' | 'declining' | 'stable'
    review_volume_trend: 'increasing' | 'decreasing' | 'stable'
  }
}

type ReviewRow = {
  location_name: string | null
  star_rating: string
  reply_comment: string | null
  reviewer_create_time: string
}

/**
 * Convert star rating string to number for calculations
 */
function convertStarRatingToNumber(rating: string): number {
  const ratingMap: Record<string, number> = {
    'ONE': 1,
    'TWO': 2,
    'THREE': 3,
    'FOUR': 4,
    'FIVE': 5
  }
  return ratingMap[rating] || 0
}

/**
 * Fetch enhanced review statistics with detailed breakdowns
 */
export async function fetchEnhancedReviewStats(): Promise<EnhancedReviewStats> {
  try {
    // Get all reviews for comprehensive analysis
    const { data: allReviews, error } = await supabase
      .from('reviews')
      .select('*')
      .order('reviewer_create_time', { ascending: false })

    if (error) throw error

    const reviews = (allReviews || []) as ReviewRow[]
    const totalReviews = reviews.length

    // Overall performance metrics
    const averageRating = reviews.length > 0 
      ? reviews.reduce((sum, review) => sum + convertStarRatingToNumber(review.star_rating), 0) / reviews.length 
      : 0

    const reviewsWithResponse = reviews.filter(r => r.reply_comment && r.reply_comment.trim().length > 0)
    const responseRate = totalReviews > 0 ? (reviewsWithResponse.length / totalReviews) * 100 : 0

    // 30-day velocity
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const reviews30d = reviews.filter(r => new Date(r.reviewer_create_time) >= thirtyDaysAgo)
    
    const sixtyDaysAgo = new Date()
    sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60)
    const reviews30to60d = reviews.filter(r => {
      const date = new Date(r.reviewer_create_time)
      return date >= sixtyDaysAgo && date < thirtyDaysAgo
    })

    const velocityChange = reviews30to60d.length > 0 
      ? ((reviews30d.length - reviews30to60d.length) / reviews30to60d.length) * 100 
      : 0

    // Rating distribution
    const ratingCounts = reviews.reduce((acc, review) => {
      const rating = convertStarRatingToNumber(review.star_rating)
      acc[rating] = (acc[rating] || 0) + 1
      return acc
    }, {} as Record<number, number>)

    const ratingDistribution = {
      five_star: { count: ratingCounts[5] || 0, percentage: totalReviews > 0 ? ((ratingCounts[5] || 0) / totalReviews) * 100 : 0 },
      four_star: { count: ratingCounts[4] || 0, percentage: totalReviews > 0 ? ((ratingCounts[4] || 0) / totalReviews) * 100 : 0 },
      three_star: { count: ratingCounts[3] || 0, percentage: totalReviews > 0 ? ((ratingCounts[3] || 0) / totalReviews) * 100 : 0 },
      two_star: { count: ratingCounts[2] || 0, percentage: totalReviews > 0 ? ((ratingCounts[2] || 0) / totalReviews) * 100 : 0 },
      one_star: { count: ratingCounts[1] || 0, percentage: totalReviews > 0 ? ((ratingCounts[1] || 0) / totalReviews) * 100 : 0 }
    }

    // Location breakdown
    const locationGroups = reviews.reduce((acc, review) => {
      const location = review.location_name || 'Unknown'
      if (!acc[location]) acc[location] = []
      acc[location].push(review)
      return acc
    }, {} as Record<string, typeof reviews>)

    const locationBreakdown: LocationStats[] = Object.entries(locationGroups).map(([locationName, locationReviews]) => {
      const locationRatingCounts = locationReviews.reduce((acc, review) => {
        const rating = convertStarRatingToNumber(review.star_rating)
        acc[rating] = (acc[rating] || 0) + 1
        return acc
      }, {} as Record<number, number>)

      const avgRating = locationReviews.length > 0
        ? locationReviews.reduce((sum, review) => sum + convertStarRatingToNumber(review.star_rating), 0) / locationReviews.length
        : 0

      const responseCount = locationReviews.filter(r => r.reply_comment && r.reply_comment.trim().length > 0).length
      const locationResponseRate = locationReviews.length > 0 ? (responseCount / locationReviews.length) * 100 : 0

      const dates = locationReviews.map(r => new Date(r.reviewer_create_time)).sort((a, b) => a.getTime() - b.getTime())

      return {
        location_name: locationName,
        total_reviews: locationReviews.length,
        average_rating: Math.round(avgRating * 10) / 10,
        five_star: locationRatingCounts[5] || 0,
        four_star: locationRatingCounts[4] || 0,
        three_star: locationRatingCounts[3] || 0,
        two_star: locationRatingCounts[2] || 0,
        one_star: locationRatingCounts[1] || 0,
        response_rate: Math.round(locationResponseRate),
        latest_review_date: dates[dates.length - 1]?.toISOString() || '',
        oldest_review_date: dates[0]?.toISOString() || ''
      }
    }).sort((a, b) => b.total_reviews - a.total_reviews)

    // Monthly trends
    const monthlyData = reviews.reduce((acc, review) => {
      const date = new Date(review.reviewer_create_time)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      
      if (!acc[monthKey]) {
        acc[monthKey] = { reviews: [], responses: 0 }
      }
      acc[monthKey].reviews.push(review)
      if (review.reply_comment && review.reply_comment.trim().length > 0) {
        acc[monthKey].responses++
      }
      return acc
    }, {} as Record<string, { reviews: typeof reviews; responses: number }>)

    const reviewsByMonth: TimeSeriesData[] = Object.entries(monthlyData)
      .map(([period, data]) => ({
        period,
        review_count: data.reviews.length,
        average_rating: data.reviews.length > 0 
          ? data.reviews.reduce((sum, r) => sum + convertStarRatingToNumber(r.star_rating), 0) / data.reviews.length 
          : 0,
        response_rate: data.reviews.length > 0 ? (data.responses / data.reviews.length) * 100 : 0
      }))
      .sort((a, b) => a.period.localeCompare(b.period))

    // Performance insights
    const topPerformingLocations = locationBreakdown
      .filter(loc => loc.total_reviews >= 5) // Minimum threshold
      .sort((a, b) => b.average_rating - a.average_rating)
      .slice(0, 3)
      .map(loc => loc.location_name)

    const locationsNeedingAttention = locationBreakdown
      .filter(loc => loc.average_rating < 4.5 || loc.response_rate < 50)
      .map(loc => loc.location_name)

    // Recent rating trend (last 3 months vs previous 3 months)
    const threeMonthsAgo = new Date()
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
    const sixMonthsAgo = new Date()
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

    const recentReviews = reviews.filter(r => new Date(r.reviewer_create_time) >= threeMonthsAgo)
    const previousReviews = reviews.filter(r => {
      const date = new Date(r.reviewer_create_time)
      return date >= sixMonthsAgo && date < threeMonthsAgo
    })

    const recentAvg = recentReviews.length > 0 
      ? recentReviews.reduce((sum, r) => sum + convertStarRatingToNumber(r.star_rating), 0) / recentReviews.length 
      : 0
    const previousAvg = previousReviews.length > 0 
      ? previousReviews.reduce((sum, r) => sum + convertStarRatingToNumber(r.star_rating), 0) / previousReviews.length 
      : 0

    const ratingTrend: 'improving' | 'declining' | 'stable' = 
      recentAvg > previousAvg + 0.1 ? 'improving' :
      recentAvg < previousAvg - 0.1 ? 'declining' : 'stable'

    const volumeTrend: 'increasing' | 'decreasing' | 'stable' =
      reviews30d.length > reviews30to60d.length * 1.1 ? 'increasing' :
      reviews30d.length < reviews30to60d.length * 0.9 ? 'decreasing' : 'stable'

    return {
      overall_performance: {
        total_reviews: totalReviews,
        average_rating: Math.round(averageRating * 10) / 10,
        response_rate: Math.round(responseRate),
        review_velocity_30d: reviews30d.length,
        review_velocity_change: Math.round(velocityChange)
      },
      location_breakdown: locationBreakdown,
      rating_distribution: ratingDistribution,
      time_analysis: {
        reviews_by_month: reviewsByMonth,
        reviews_by_quarter: [], // TODO: Implement if needed
        reviews_by_year: [], // TODO: Implement if needed
        peak_review_days: [], // TODO: Implement day-of-week analysis
        response_time_analysis: {
          average_response_time_hours: 0, // TODO: Calculate when we have response timestamps
          median_response_time_hours: 0,
          overdue_responses: reviews.filter(r => !r.reply_comment).length
        }
      },
      performance_insights: {
        top_performing_locations: topPerformingLocations,
        locations_needing_attention: locationsNeedingAttention,
        recent_rating_trends: ratingTrend,
        review_volume_trend: volumeTrend
      }
    }

  } catch (error) {
    console.error('Error fetching enhanced review stats:', error)
    throw new Error('Failed to fetch enhanced review statistics')
  }
}
